// Мост между React и Observable Plot.
//
// ПОЧЕМУ ЭТО ВООБЩЕ ОТДЕЛЬНЫЙ ФАЙЛ. Plot — императивная библиотека:
// `Plot.plot(options)` возвращает готовый DOM-узел, а не элемент React.
// Вставлять его надо руками и убирать за собой тоже руками, иначе при
// каждой перерисовке в контейнер добавляется ещё один график, и через
// десяток движений ползунка страница набирает десяток невидимых SVG.
// Один мост на всё приложение вместо десяти useEffect по компонентам.
//
// ШИРИНА ИЗМЕРЯЕТСЯ, А НЕ ЗАДАЁТСЯ. Ровно этим новые графики отличаются
// от прежнего рукописного SVG: тот жил в фиксированном viewBox шириной
// 480 и на широком мониторе рисовался в середине пустого поля, потому
// что `preserveAspectRatio` вписывал картинку по высоте. Plot получает
// настоящую ширину контейнера от ResizeObserver и раскладывает оси
// заново — график занимает всё, что ему дали, при любой ширине окна.
//
// ЦВЕТА НЕ ДУБЛИРУЮТСЯ В JS. Plot считает строку `var(--series-a)`
// допустимым цветом и кладёт её в атрибут SVG как есть, поэтому палитра
// остаётся ровно там же, где была, — в styles.css, под тестом контраста.
// Библиотека, которую пришлось бы кормить хексами из JS, завела бы
// второй источник правды о цвете и разошлась бы со светлой темой в
// первый же день.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as Plot from '@observablehq/plot';

/** Опции Plot.plot без ширины: её всегда подставляет сам мост. */
export type PlotOptions = Omit<Parameters<typeof Plot.plot>[0] & object, 'width'>;

export interface PlotFigureProps {
  /** Готовые опции Plot. Должны быть мемоизированы: иначе график
      пересобирается на каждый кадр React и теряет наведённую подсказку. */
  options: PlotOptions;
  /** Фиксированная ширина в пикселях. Без неё ширину даёт контейнер. */
  width?: number;
  className?: string;
  /**
   * Выделение области по горизонтали. Получает интервал в единицах
   * данных — то, что под курсором, а не пиксели.
   */
  onSelect?: (from: number, to: number) => void;
  /** Двойной щелчок по графику. Обычно — сброс зума. */
  onResetSelection?: () => void;
  /** Подпись для скринридера: у графика её иначе нет вовсе. */
  ariaLabel?: string;
}

/** Меньше этого числа пикселей протяжка считается промахом, а не выделением. */
const MIN_DRAG_PX = 6;

export function PlotFigure({
  options,
  width,
  className,
  onSelect,
  onResetSelection,
  ariaLabel,
}: PlotFigureProps) {
  const host = useRef<HTMLDivElement | null>(null);
  const figure = useRef<(HTMLElement & Plot.Plot) | null>(null);
  const brush = useRef<HTMLDivElement | null>(null);
  const [measured, setMeasured] = useState<number>(width ?? 0);

  // Протяжка живёт в ref и правит стиль рамки напрямую, а НЕ в состоянии
  // React.
  //
  // Причина не в скорости. Состояние вызвало бы перерисовку родителя, тот
  // пересобрал бы объект опций — и эффект ниже снёс бы график вместе с
  // элементом, на котором держится захват указателя. Протяжка обрывалась
  // бы на первом же движении мыши, причём молча: события есть,
  // обработчики есть, а цели у них уже нет.
  const drag = useRef<{ from: number; to: number } | null>(null);

  // Ширина контейнера. useLayoutEffect, а не useEffect: первая отрисовка
  // с нулевой шириной успела бы моргнуть пустым местом.
  useLayoutEffect(() => {
    if (width !== undefined) {
      setMeasured(width);
      return;
    }
    const element = host.current;
    if (element === null) return;
    const update = () => setMeasured(Math.max(0, Math.floor(element.clientWidth)));
    update();
    if (typeof ResizeObserver !== 'function') return;
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [width]);

  useEffect(() => {
    const element = host.current;
    if (element === null || measured <= 0) return;
    const plot = Plot.plot({ ...options, width: measured }) as HTMLElement & Plot.Plot;
    if (ariaLabel !== undefined) plot.setAttribute('aria-label', ariaLabel);
    element.append(plot);
    figure.current = plot;
    return () => {
      plot.remove();
      figure.current = null;
    };
  }, [options, measured, ariaLabel]);

  // Протяжка мышью → интервал в единицах данных.
  //
  // Колёсико намеренно НЕ трогаем: страница длинная, и график, крадущий
  // прокрутку под курсором, ломает чтение таблиц ради удобства, которым
  // пользуются раз в сеанс. Выделение области и двойной щелчок дают то же
  // самое, но только когда их попросили.
  const pixelToValue = useCallback((clientX: number): number | null => {
    const plot = figure.current;
    if (plot === null) return null;
    const scale = plot.scale('x');
    if (!scale || typeof scale.invert !== 'function') return null;
    const box = plot.getBoundingClientRect();
    return scale.invert(clientX - box.left) as number;
  }, []);

  const paintBrush = useCallback(() => {
    const element = brush.current;
    if (element === null) return;
    const current = drag.current;
    if (current === null || Math.abs(current.to - current.from) < MIN_DRAG_PX) {
      element.hidden = true;
      return;
    }
    element.hidden = false;
    element.style.left = `${Math.min(current.from, current.to)}px`;
    element.style.width = `${Math.abs(current.to - current.from)}px`;
  }, []);

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (onSelect === undefined || event.button !== 0) return;
      const box = event.currentTarget.getBoundingClientRect();
      const at = event.clientX - box.left;
      drag.current = { from: at, to: at };
      paintBrush();
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [onSelect, paintBrush],
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const current = drag.current;
      if (current === null) return;
      const box = event.currentTarget.getBoundingClientRect();
      drag.current = { from: current.from, to: event.clientX - box.left };
      paintBrush();
    },
    [paintBrush],
  );

  const finishDrag = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const current = drag.current;
      drag.current = null;
      paintBrush();
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      if (current === null || onSelect === undefined) return;
      const box = event.currentTarget.getBoundingClientRect();
      const lo = Math.min(current.from, current.to);
      const hi = Math.max(current.from, current.to);
      if (hi - lo < MIN_DRAG_PX) return;
      const from = pixelToValue(box.left + lo);
      const to = pixelToValue(box.left + hi);
      if (from === null || to === null || !Number.isFinite(from) || !Number.isFinite(to)) return;
      onSelect(Math.min(from, to), Math.max(from, to));
    },
    [onSelect, paintBrush, pixelToValue],
  );

  return (
    <div
      ref={host}
      className={className === undefined ? 'plotfig' : `plotfig ${className}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finishDrag}
      onPointerCancel={finishDrag}
      onDoubleClick={onResetSelection}
      style={onSelect ? { cursor: 'ew-resize' } : undefined}
    >
      {/* Рамка выделения живёт всегда и прячется атрибутом hidden:
          график вставляется в этот же контейнер вручную, и добавлять
          сюда узлы через React во время протяжки значило бы каждый раз
          заново примирять содержимое, которое React не создавал. */}
      <div ref={brush} className="plot-brush" hidden />
    </div>
  );
}
